/**
 * Shared native cart mutation queue, checkout lock, and shared cart HTML apply.
 * App recommendation code may call window.CaffeineCart.queueMutation with a
 * fetch-only callback; the theme settlement hook refreshes native cart HTML
 * before checkout unlocks. The app still snapshots and emits
 * caffeine:cart:external-change after the queue promise resolves.
 */
(function (root) {
  var CHECKOUT_PENDING_CLASS = 'caffeine-cart-pending';
  var CHECKOUT_SELECTOR = [
    '.cart__checkout',
    '[name="checkout"]',
    '.additional-checkout-buttons',
    '.additional-checkout-buttons a',
    '.additional-checkout-buttons button',
    '.additional-checkout-buttons input',
    '.shopify-payment-button',
    '.shopify-payment-button__button',
    'shopify-accelerated-checkout',
    'shopify-accelerated-checkout-cart',
    'redo-shopify-toggle'
  ].join(',');
  // Redo renders its Checkout+ buttons inside this host's shadow root, so the
  // theme cannot disable them directly. Clicks are blocked at the document.
  var REDO_CHECKOUT_HOST = 'REDO-SHOPIFY-TOGGLE';

  function createRenderCoordinator() {
    var epoch = 0;
    var views = [];

    function subscribe(view) {
      if (!view || views.indexOf(view) !== -1) return function () {};
      views.push(view);
      return function unsubscribe() {
        views = views.filter(function (item) {
          return item !== view;
        });
      };
    }

    function beginWork() {
      epoch += 1;
      return epoch;
    }

    function isCurrent(token) {
      return token === epoch;
    }

    function applyIfCurrent(token, text) {
      if (token !== epoch) return false;
      var ok = true;
      views.forEach(function (view) {
        if (typeof view.applyMarkup === 'function') {
          if (view.applyMarkup(text) === false) ok = false;
        }
      });
      return ok;
    }

    function failIfCurrent(token, error) {
      if (token !== epoch) return false;
      views.forEach(function (view) {
        if (typeof view.onRefreshError === 'function') view.onRefreshError(error);
      });
      return true;
    }

    var settledTokens = {};

    function markSettled(token) {
      if (token != null) settledTokens[token] = true;
    }

    function isSettled(token) {
      return !!settledTokens[token];
    }

    return {
      subscribe: subscribe,
      beginWork: beginWork,
      getEpoch: function () {
        return epoch;
      },
      isCurrent: isCurrent,
      applyIfCurrent: applyIfCurrent,
      failIfCurrent: failIfCurrent,
      markSettled: markSettled,
      isSettled: isSettled,
      getViewCount: function () {
        return views.length;
      }
    };
  }

  function createMutationQueue(options) {
    options = options || {};
    var coordinator = options.coordinator || createRenderCoordinator();
    var settlementHook = typeof options.settlementHook === 'function' ? options.settlementHook : null;
    var chain = Promise.resolve();
    var pending = 0;
    var lastCart = null;

    function setSettlementHook(fn) {
      settlementHook = typeof fn === 'function' ? fn : null;
    }

    function settleIfNeeded(token, context) {
      if (coordinator.isSettled(token)) {
        return Promise.resolve({ skipped: true });
      }
      if (typeof settlementHook !== 'function') {
        coordinator.markSettled(token);
        return Promise.resolve({ skipped: true });
      }
      return Promise.resolve()
        .then(function () {
          return settlementHook(token, context || {});
        })
        .then(function (result) {
          coordinator.markSettled(token);
          return result;
        }, function (err) {
          coordinator.markSettled(token);
          throw err;
        });
    }

    function setCheckoutLocked(locked) {
      if (typeof document === 'undefined') return;
      document.documentElement.classList.toggle(CHECKOUT_PENDING_CLASS, locked);
      document.querySelectorAll(CHECKOUT_SELECTOR).forEach(function (el) {
        if (locked) {
          if (!el.hasAttribute('data-caffeine-lock')) {
            el.setAttribute('data-caffeine-lock', '1');
            el.setAttribute('data-caffeine-lock-disabled', el.disabled ? '1' : '0');
            var prevAria = el.getAttribute('aria-disabled');
            el.setAttribute('data-caffeine-lock-aria', prevAria == null ? '' : prevAria);
            if ('disabled' in el) el.disabled = true;
            el.setAttribute('aria-disabled', 'true');
          }
        } else if (el.getAttribute('data-caffeine-lock') === '1') {
          var wasDisabled = el.getAttribute('data-caffeine-lock-disabled') === '1';
          var wasAria = el.getAttribute('data-caffeine-lock-aria');
          if ('disabled' in el) el.disabled = wasDisabled;
          if (wasAria) {
            el.setAttribute('aria-disabled', wasAria);
          } else {
            el.removeAttribute('aria-disabled');
          }
          el.removeAttribute('data-caffeine-lock');
          el.removeAttribute('data-caffeine-lock-disabled');
          el.removeAttribute('data-caffeine-lock-aria');
        }
      });
    }

    function queueMutation(task) {
      if (typeof task !== 'function') {
        return Promise.reject(new Error('queueMutation requires a function'));
      }

      pending += 1;
      var token = coordinator.beginWork();
      // Cart as it was just before this task ran, so settlement can tell an
      // add from an attribute write, note save or plain refresh.
      var context = { before: null };
      setCheckoutLocked(true);

      var run = chain.then(
        function () {
          context.before = lastCart;
          return Promise.resolve().then(function () {
            return task(token);
          });
        },
        function () {
          context.before = lastCart;
          return Promise.resolve().then(function () {
            return task(token);
          });
        }
      );

      var finished = run.then(
        function (value) {
          return settleIfNeeded(token, context).then(
            function () {
              return value;
            },
            function () {
              return value;
            }
          );
        },
        function (err) {
          return settleIfNeeded(token, context).then(
            function () {
              throw err;
            },
            function () {
              throw err;
            }
          );
        }
      );

      chain = finished.then(
        function () {},
        function () {}
      );

      return finished.finally(function () {
        pending = Math.max(0, pending - 1);
        if (pending === 0) {
          setCheckoutLocked(false);
        }
      });
    }

    function preventCheckoutIfPending(evt) {
      if (pending > 0) {
        if (evt && typeof evt.preventDefault === 'function') evt.preventDefault();
        return true;
      }
      return false;
    }

    return {
      queueMutation: queueMutation,
      isPending: function () {
        return pending > 0;
      },
      getPendingCount: function () {
        return pending;
      },
      setCheckoutLocked: setCheckoutLocked,
      preventCheckoutIfPending: preventCheckoutIfPending,
      coordinator: coordinator,
      setSettlementHook: setSettlementHook,
      markSettled: coordinator.markSettled,
      isSettled: coordinator.isSettled,
      getLastCart: function () {
        return lastCart;
      },
      setLastCart: function (cart) {
        lastCart = cart;
        return cart;
      }
    };
  }

  function parseJsonResponse(response, text) {
    var json;
    try {
      json = text ? JSON.parse(text) : null;
    } catch (err) {
      var parseError = new Error('invalid_json');
      parseError.code = 'invalid_json';
      parseError.httpStatus = response && response.status;
      parseError.uncertain = true;
      parseError.cause = err;
      throw parseError;
    }

    var httpStatus = response && response.status;
    var failed =
      (response && response.ok === false) ||
      (json && (json.status === 422 || json.status === '422'));

    if (failed) {
      var serverError = new Error(
        (json && (json.description || json.message)) || 'cart_request_failed'
      );
      serverError.httpStatus = httpStatus || (json && json.status) || 422;
      serverError.payload = json;
      serverError.code = 'server_reject';
      throw serverError;
    }

    return json;
  }

  function findLineByVariantId(cart, variantId) {
    if (!cart || !Array.isArray(cart.items) || variantId == null || variantId === '') {
      return null;
    }
    var matchId = String(variantId);
    for (var i = 0; i < cart.items.length; i += 1) {
      var item = cart.items[i];
      if (item && String(item.variant_id) === matchId) {
        return item;
      }
    }
    return null;
  }

  function quantityForVariant(cart, variantId) {
    var line = findLineByVariantId(cart, variantId);
    return line ? Number(line.quantity) || 0 : 0;
  }

  function isFullCart(value) {
    return !!(value && Array.isArray(value.items));
  }

  var coordinator = createRenderCoordinator();
  var api = createMutationQueue({ coordinator: coordinator });
  api.createMutationQueue = createMutationQueue;
  api.createRenderCoordinator = createRenderCoordinator;
  api.parseJsonResponse = parseJsonResponse;
  api.findLineByVariantId = findLineByVariantId;
  api.quantityForVariant = quantityForVariant;
  api.isFullCart = isFullCart;
  api.subscribeView = coordinator.subscribe;
  api.beginWork = coordinator.beginWork;
  api.applyIfCurrent = coordinator.applyIfCurrent;
  api.failIfCurrent = coordinator.failIfCurrent;
  api.isCurrent = coordinator.isCurrent;
  api.markSettled = coordinator.markSettled;
  api.isSettled = coordinator.isSettled;
  Object.defineProperty(api, 'lastCart', {
    get: function () {
      return api.getLastCart();
    },
    set: function (cart) {
      api.setLastCart(cart);
    }
  });

  api.fetchFreshCart = function () {
    if (root.theme && root.theme.cart && typeof root.theme.cart.getCart === 'function') {
      return root.theme.cart.getCart();
    }
    var url = (root.theme && root.theme.routes && root.theme.routes.cart) || '/cart.js';
    var join = url.indexOf('?') === -1 ? '?' : '&';
    return root.fetch(url + join + 't=' + Date.now(), {
      credentials: 'same-origin',
      method: 'GET',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      return response.text().then(function (text) {
        return parseJsonResponse(response, text);
      });
    });
  };

  api.refreshViews = function (fetchMarkup, token) {
    var applyToken = token == null ? coordinator.getEpoch() : token;
    return Promise.all([
      api.fetchFreshCart(),
      Promise.resolve().then(fetchMarkup)
    ]).then(function (results) {
      if (!coordinator.isCurrent(applyToken)) {
        coordinator.markSettled(applyToken);
        return { stale: true };
      }
      var cart = results[0];
      var text = results[1];
      if (!api.isFullCart(cart)) {
        var badCart = new Error('refresh_failed');
        badCart.code = 'refresh_failed';
        coordinator.failIfCurrent(applyToken, badCart);
        throw badCart;
      }
      if (!text) {
        var empty = new Error('refresh_failed');
        empty.code = 'refresh_failed';
        coordinator.failIfCurrent(applyToken, empty);
        throw empty;
      }
      var applied = coordinator.applyIfCurrent(applyToken, text);
      if (!applied) {
        if (!coordinator.isCurrent(applyToken)) {
          coordinator.markSettled(applyToken);
          return { stale: true };
        }
        var badHtml = new Error('refresh_failed');
        badHtml.code = 'refresh_failed';
        coordinator.failIfCurrent(applyToken, badHtml);
        throw badHtml;
      }
      api.setLastCart(cart);
      if (typeof document !== 'undefined' && document.dispatchEvent) {
        document.dispatchEvent(new CustomEvent('caffeine:cart:changed', {
          bubbles: true,
          detail: { cart: cart }
        }));
      }
      coordinator.markSettled(applyToken);
      return { stale: false, text: text, cart: cart };
    }).catch(function (err) {
      coordinator.markSettled(applyToken);
      if (err && err.stale) throw err;
      if (err && err.code === 'refresh_failed') throw err;
      coordinator.failIfCurrent(applyToken, err);
      throw err;
    });
  };

  function cartSignature(cart) {
    if (!isFullCart(cart)) return '';
    return (cart.items || []).map(function (item) {
      return [item.key || '', item.variant_id || '', item.quantity || 0].join(':');
    }).join('|') + '#' + String(cart.item_count != null ? cart.item_count : '') + '#' + String(cart.total_price != null ? cart.total_price : '');
  }

  function openNativeCart() {
    if (typeof document === 'undefined' || !document.dispatchEvent) return;
    document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
      bubbles: true,
      detail: { settled: true, source: 'recommendations' }
    }));
  }

  function itemCount(cart) {
    return isFullCart(cart) && typeof cart.item_count === 'number' ? cart.item_count : null;
  }

  // Open the drawer only when the settled task added items. Attribute writes
  // (such as the learning journey), note saves and background refreshes must
  // never pop the drawer open on page load.
  function defaultSettlement(token, context) {
    if (!root.theme || !root.theme.cart || typeof root.theme.cart.getCartProductMarkup !== 'function') {
      return Promise.resolve({ skipped: true });
    }
    return api.refreshViews(function () {
      return root.theme.cart.getCartProductMarkup().then(function (payload) {
        return payload && payload.text;
      });
    }, token).then(function (result) {
      var before = itemCount(context && context.before);
      var after = itemCount(result && result.cart);
      if (result && result.stale === false && before !== null && after !== null && after > before) {
        openNativeCart();
      }
      return result;
    });
  }

  api.setSettlementHook(defaultSettlement);

  function installDocumentListeners() {
    if (typeof document === 'undefined' || installDocumentListeners.bound) return;
    installDocumentListeners.bound = true;
    document.addEventListener('caffeine:cart:external-change', function (evt) {
      if (api._handlingExternal) return;
      var detail = (evt && evt.detail) || {};
      var cart = detail.cart;
      if (api.isFullCart(cart) && api.getLastCart() && cartSignature(cart) === cartSignature(api.getLastCart())) {
        if (detail.source === 'pdp') openNativeCart();
        return;
      }
      api._handlingExternal = true;
      api.queueMutation(function (token) {
        if (!root.theme || !root.theme.cart || typeof root.theme.cart.getCartProductMarkup !== 'function') {
          if (api.isFullCart(cart)) {
            api.setLastCart(cart);
          }
          return;
        }
        return api.refreshViews(function () {
          return root.theme.cart.getCartProductMarkup().then(function (payload) {
            return payload && payload.text;
          });
        }, token);
      }).then(function () {
        if (detail.source === 'pdp') openNativeCart();
      }).catch(function () {
      }).finally(function () {
        api._handlingExternal = false;
      });
    });
    document.addEventListener('cart:build', function () {
      if (!root.theme || !root.theme.cart || typeof root.theme.cart.getCartProductMarkup !== 'function') return;
      api.queueMutation(function (token) {
        return api.refreshViews(function () {
          return root.theme.cart.getCartProductMarkup().then(function (payload) {
            return payload && payload.text;
          });
        }, token);
      }).catch(function () {});
    });

    function isInsideRedoCheckout(evt) {
      var path = typeof evt.composedPath === 'function' ? evt.composedPath() : [];
      for (var i = 0; i < path.length; i += 1) {
        if (path[i] && path[i].nodeName === REDO_CHECKOUT_HOST) return true;
      }
      return false;
    }

    function blockRedoCheckoutWhilePending(evt) {
      if (!api.isPending() || !isInsideRedoCheckout(evt)) return;
      if (evt.type === 'keydown' && evt.key !== 'Enter' && evt.key !== ' ') return;
      evt.preventDefault();
      evt.stopImmediatePropagation();
    }

    ['click', 'keydown', 'submit'].forEach(function (type) {
      document.addEventListener(type, blockRedoCheckoutWhilePending, true);
    });
  }
  installDocumentListeners();

  function publishApi(target) {
    target = target || {};
    Object.keys(api).forEach(function (key) {
      if (key === 'lastCart') return;
      target[key] = api[key];
    });
    Object.defineProperty(target, 'lastCart', {
      configurable: true,
      enumerable: true,
      get: function () {
        return api.getLastCart();
      },
      set: function (cart) {
        api.setLastCart(cart);
      }
    });
    return target;
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  root.CaffeineCart = publishApi(root.CaffeineCart || {});
})(typeof window !== 'undefined' ? window : globalThis);
